import React from 'react';
import { Card, Tooltip, Divider } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  ComposedChart,
  Cell,
  LabelList
} from "recharts";

const CombinedAnalysisChart = ({ sourceData, sourceLoading, showUniqueData = true }) => {
  const hasData = sourceData && sourceData.length > 0;
  
  let topSources = [];
  let overallRate = 0;
  
  if (hasData) {
    const sortedSourceData = [...sourceData].sort((a, b) => 
      (b.total_bookings + b.total_abandonments) - (a.total_bookings + a.total_abandonments)
    );
    topSources = sortedSourceData.slice(0, 10).map(item => {
      const source = item.lead_source || '(not set)';
      const medium = item.lead_medium || '(not set)';
      const campaign = item.lead_campaign || '(not set)';
      const truncatedSource = source.length > 12 ? source.substring(0, 9) + '...' : source;
      const truncatedMedium = medium.length > 12 ? medium.substring(0, 9) + '...' : medium;
      const truncatedCampaign = campaign.length > 12 ? campaign.substring(0, 9) + '...' : campaign;
      return {
        ...item,
        source_medium_campaign_display: `${truncatedSource} / ${truncatedMedium} / ${truncatedCampaign}`,
        full_source_medium_campaign: `${source} / ${medium} / ${campaign}` // Keep full text for tooltip
      };
    });
    
    const totalBookings = sourceData.reduce((sum, item) => sum + item.total_bookings, 0);
    const totalLeads = sourceData.reduce((sum, item) => sum + (item.total_bookings + item.total_abandonments), 0);
    overallRate = totalLeads > 0 ? (totalBookings / totalLeads) * 100 : 0;
  }

  const dataType = showUniqueData ? 'Unique' : 'Total';

  return (
    <div>
      <Card 
        title={`Source, Medium & Campaign Analysis with ${dataType} Booking & Abandonment Rates`}
        loading={sourceLoading}
        extra={
          <Tooltip title={`This chart shows the distribution of ${dataType.toLowerCase()} bookings, ${dataType.toLowerCase()} abandonments, ${dataType.toLowerCase()} booking rates, and ${dataType.toLowerCase()} abandonment rates by source, medium, and campaign for the selected date range.`}>
            <InfoCircleOutlined style={{ fontSize: '16px' }} />
          </Tooltip>
        }
      >
        {!hasData && !sourceLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            No source data available
          </div>
        ) : (
          <div style={{ width: '100%', height: 500 }}>
            <ResponsiveContainer width="100%" height="120%">
              <ComposedChart
                data={topSources}
                margin={{
                  top: 40,
                  right: 80,
                  left: 20,
                  bottom: 140,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="source_medium_campaign_display" 
                  angle={-45} 
                  textAnchor="end"
                  height={150}
                  interval={0}
                  tick={{ fontSize: 11 }}
                />
                <YAxis yAxisId="left" orientation="left" label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: `${dataType} Rate (%)`, angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }} />
                <RechartsTooltip 
                  formatter={(value, name) => {
                    if (name === 'total_bookings') return [value, `${dataType} Bookings`];
                    if (name === 'total_abandonments') return [value, `${dataType} Abandonments`];
                    if (name === 'booking_rate_percentage') return [`${value.toFixed(2)}%`, `${dataType} Booking Rate`];
                    if (name === 'abandonment_rate_percentage') return [`${value.toFixed(2)}%`, `${dataType} Abandonment Rate`];
                    return [value, name];
                  }}
                  labelFormatter={(label, payload) => {
                    if (payload && payload.length > 0) {
                      return `Source / Medium / Campaign: ${payload[0].payload.full_source_medium_campaign || label}`;
                    }
                    return `Source / Medium / Campaign: ${label}`;
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="total_bookings" name={`${dataType} Bookings`} fill="#3B7AD9" />
                <Bar yAxisId="left" dataKey="total_abandonments" name={`${dataType} Abandonments`} fill="#FF4D4F" />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="booking_rate_percentage" 
                  stroke="#52c41a" 
                  strokeWidth={3}
                  name={`${dataType} Booking Rate (%)`}
                  dot={{ r: 5, fill: '#52c41a' }}
                  activeDot={{ r: 7, fill: '#52c41a' }}
                />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="abandonment_rate_percentage" 
                  stroke="#ff7875" 
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  name={`${dataType} Abandonment Rate (%)`}
                  dot={{ r: 5, fill: '#ff7875' }}
                  activeDot={{ r: 7, fill: '#ff7875' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Divider />
    </div>
  );
};

export default CombinedAnalysisChart; 